import { gql, useApolloClient, useQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRxDB } from 'rxdb-hooks';

import { USER_MAILBOXES_ID } from '@work4all/data/lib/db';
import { useTenant } from '@work4all/data/lib/hooks/routing/TenantProvider';
import { useSetting } from '@work4all/data/lib/settings';

import { Mailbox } from '@work4all/models/lib/Classes/Mailbox.entity';
import { MailboxFolder } from '@work4all/models/lib/Classes/MailboxFolder.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { MailboxLoadErrorType } from '@work4all/models/lib/Enums/MailboxLoadErrorType.enum';

import { settings } from '../settings';

const READ_MAIL_CHILD_FOLDERS_FLAT = gql`
  query ReadMailboxChildFoldersFlat($mailboxId: ID!, $parentId: String) {
    readMailboxChildFoldersFlat(mailboxId: $mailboxId, parentId: $parentId) {
      childFolderCount
      folderType
      id
      index
      name
      parentId
      unassignedItemsCount
      unreadItemsCount
    }
  }
`;

const READ_MAILBOXES_FLAT = gql`
  query ReadMailboxesFlat {
    readMailboxesFlat {
      folder {
        folderType
        id
        index
        childFolderCount
        name
        parentId
        unassignedItemsCount
        unreadItemsCount
      }
      id
      loadErrorMessage
      loadErrorType
      mailboxPrimaryAddress
      mailboxType
    }
  }
`;

export const useReadMailboxChildFoldersFlat = (props: {
  parentId: string;
  mailboxId: string;
}) => {
  return useQuery<{
    readMailboxChildFoldersFlat: MailboxFolder<EMode.entity>[];
  }>(READ_MAIL_CHILD_FOLDERS_FLAT, {
    variables: {
      parentId: props.parentId,
      mailboxId: props.mailboxId,
    },
    skip: !props.mailboxId || !props.parentId,
  });
};

export const useReadMailboxesFlat = () => {
  return useQuery<{ readMailboxesFlat: Mailbox<EMode.entity>[] }>(
    READ_MAILBOXES_FLAT
  );
};

// Hook for loading folders on demand when expanded (for settings sidebar)
export function useMailboxFoldersOnDemand(mailboxId: string | null) {
  const client = useApolloClient();
  const [loadedFolders, setLoadedFolders] = useState<
    Record<string, MailboxFolder[]>
  >({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );

  const loadFolderChildren = useCallback(
    async (folderId: string) => {
      if (!mailboxId || loadedFolders[folderId]) {
        return; // Already loaded or no mailbox
      }

      setLoadingStates((prev) => ({ ...prev, [folderId]: true }));

      try {
        const subFoldersRes = await client.query({
          query: READ_MAIL_CHILD_FOLDERS_FLAT,
          variables: {
            mailboxId,
            parentId: folderId,
          },
        });

        const subFolders =
          subFoldersRes.data?.readMailboxChildFoldersFlat || [];

        setLoadedFolders((prev) => ({ ...prev, [folderId]: subFolders }));
      } catch (error) {
        console.error('Error loading folder children:', error);
      } finally {
        setLoadingStates((prev) => ({ ...prev, [folderId]: false }));
      }
    },
    [mailboxId, client, loadedFolders]
  );

  const getFolderChildren = useCallback(
    (folderId: string): MailboxFolder[] => {
      return loadedFolders[folderId] || [];
    },
    [loadedFolders]
  );

  const isLoading = useCallback(
    (folderId: string): boolean => {
      return loadingStates[folderId] || false;
    },
    [loadingStates]
  );

  return {
    loadFolderChildren,
    getFolderChildren,
    isLoading,
  };
}

export function useMailboxesData(loadDeepFolders = true, showError = false) {
  const db = useRxDB();
  const { activeTenant } = useTenant();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const setting = useSetting(settings.mailboxFolderVisibility());

  const client = useApolloClient();

  const [flatMailboxes, setFlatMailboxes] = useState<Mailbox[]>([]);
  const [completeMailboxes, setCompleteMailboxes] = useState<Mailbox[]>([]);

  const mailboxesRes = useReadMailboxesFlat();

  const getLocalMailboxes = useCallback(async () => {
    const localMailboxes = await db.getLocal(
      `${USER_MAILBOXES_ID}${activeTenant}`
    );
    setCompleteMailboxes(localMailboxes?._data?.data || []);
  }, [activeTenant, db]);

  const loadAdditionalFolders = useCallback(
    async (mailboxes: Mailbox[]) => {
      // Only query child folders for folders that are explicitly visible and have known children
      function filterVisibleParentFolders(
        folders: MailboxFolder<EMode.entity>[],
        mailboxId: string
      ) {
        return folders
          .filter(
            (subFolder) =>
              subFolder.childFolderCount > 0 &&
              !!setting.value[mailboxId]?.[subFolder.id]
          )
          .map((subFolder) => getSubFolders(mailboxId, subFolder.id));
      }

      async function getSubFolders(
        mailboxId: string,
        folderId: string
      ): Promise<MailboxFolder[]> {
        const subFoldersRes = await client.query({
          query: READ_MAIL_CHILD_FOLDERS_FLAT,
          variables: {
            mailboxId,
            parentId: folderId,
          },
        });

        let subFolders = subFoldersRes.data?.readMailboxChildFoldersFlat;

        const promises = filterVisibleParentFolders(subFolders, mailboxId);

        const resAll = await Promise.all(promises);
        for (const subChildFolders of resAll) {
          subFolders = subFolders.concat(subChildFolders);
        }

        return subFolders;
      }

      const resultingMailboxes = [];

      for (const mailbox of mailboxes) {
        const originalFolders = [...(mailbox.folder ?? [])];
        let additionalFolders = [];

        const promises = filterVisibleParentFolders(
          originalFolders,
          mailbox.id
        );

        const resAll = await Promise.all(promises);
        for (const subFolders of resAll) {
          additionalFolders = additionalFolders.concat(subFolders);
        }

        const uniqueFolders = [...originalFolders, ...additionalFolders].reduce(
          (acc, obj) => {
            if (!acc.some((o) => o.id === obj.id)) {
              acc.push(obj);
            }
            return acc;
          },
          []
        );

        resultingMailboxes.push({
          ...mailbox,
          folder: uniqueFolders,
        });
      }

      setCompleteMailboxes(resultingMailboxes);
    },
    [client, setting.value]
  );

  useEffect(() => {
    getLocalMailboxes();
  }, []);

  useEffect(() => {
    db.upsertLocal(
      `${USER_MAILBOXES_ID}${activeTenant}`,
      completeMailboxes.length ? completeMailboxes : []
    );
  }, [activeTenant, db, completeMailboxes]);

  useEffect(() => {
    if (!mailboxesRes.loading) {
      setFlatMailboxes(mailboxesRes.data?.readMailboxesFlat || []);
      if (loadDeepFolders) {
        loadAdditionalFolders(mailboxesRes.data?.readMailboxesFlat || []);
      }
    }
  }, [loadAdditionalFolders, mailboxesRes, loadDeepFolders]);

  useEffect(() => {
    if (!showError || mailboxesRes.loading) return;

    const data = mailboxesRes.data.readMailboxesFlat[0];

    if (data?.loadErrorType === MailboxLoadErrorType.AUTH_ERROR) {
      enqueueSnackbar(t('ERROR.READ_MAILBOXES_FLAT.AUTH_ERROR'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  }, [showError, mailboxesRes, enqueueSnackbar, t]);

  const refetchMailboxes = useCallback(() => {
    mailboxesRes.refetch();
  }, [mailboxesRes]);

  return {
    mailboxes: completeMailboxes.length ? completeMailboxes : flatMailboxes,
    refetch: refetchMailboxes,
  };
}
